import { useTranslation } from 'react-i18next';
import { AudioButton, ExerciseCard } from '@fluent/ui';
import { useAudio } from '../audio/audioPlayer.js';
import { GrammarVideo } from './GrammarVideo.js';
import type { ReviewBlock } from './reviewContent.js';
import './ReviewCard.css';

export interface ReviewCardProps {
  block: ReviewBlock;
}

/**
 * ReviewCard — the single read-only renderer for a teaching block. Shared by
 * the LessonRecap (Feature 2) and the LessonPlayer's past-exercise review
 * (Feature 1) so there is exactly one read-only look. Token-styled to match
 * the live exercise cards (see PhraseIntro.css). Never interactive beyond the
 * optional AudioButton — there is nothing to answer.
 */
export function ReviewCard({ block }: ReviewCardProps) {
  const { t } = useTranslation();
  switch (block.kind) {
    case 'grammar':
      return (
        <ExerciseCard kicker={t('lesson.grammar_tip.kicker')}>
          <div className="fluent-review fluent-review--grammar">
            <span className="fluent-review__badge" aria-hidden="true">💡</span>
            {block.titleKa && (
              <p className="fluent-review__grammar-title" lang="ka">
                {block.titleKa}
              </p>
            )}
            {block.videoKey && <GrammarVideo videoKey={block.videoKey} title={block.titleKa} />}
            <p className="fluent-review__grammar-body" lang="ka">
              {block.ka}
            </p>
          </div>
        </ExerciseCard>
      );
    case 'phrase':
      return (
        <ExerciseCard kicker={t('lesson.review.kicker')}>
          <PhraseReview block={block} />
        </ExerciseCard>
      );
    case 'dialogue':
      return (
        <ExerciseCard kicker={t('lesson.dialogue.kicker')}>
          <div className="fluent-review fluent-review--dialogue">
            {block.noteKa && (
              <p className="fluent-review__note" lang="ka">
                {block.noteKa}
              </p>
            )}
            <ol className="fluent-review__lines">
              {block.lines.map((line, i) => (
                <DialogueLineReview key={`${block.key}-${i}`} line={line} />
              ))}
            </ol>
          </div>
        </ExerciseCard>
      );
  }
}

function PhraseReview({ block }: { block: Extract<ReviewBlock, { kind: 'phrase' }> }) {
  const { t } = useTranslation();
  const audio = useAudio(block.audioKey ?? '');
  return (
    <div className="fluent-review fluent-review--phrase">
      <p className="fluent-review__en" lang="en">
        {block.en}
      </p>
      {block.audioKey && (
        <AudioButton
          size="sm"
          playing={audio.playing}
          onClick={() => (audio.playing ? audio.stop() : audio.play())}
          label={t('lesson.review.audio_aria')}
        />
      )}
      {block.ka && (
        <p className="fluent-review__ka" lang="ka">
          {block.ka}
        </p>
      )}
      {block.noteKa && (
        <p className="fluent-review__note" lang="ka">
          {block.noteKa}
        </p>
      )}
    </div>
  );
}

function DialogueLineReview({
  line,
}: {
  line: Extract<ReviewBlock, { kind: 'dialogue' }>['lines'][number];
}) {
  const { t } = useTranslation();
  const audio = useAudio(line.audioKey ?? '');
  return (
    <li className="fluent-review__line" data-speaker={line.speaker.toLowerCase()}>
      <span className="fluent-review__speaker">{line.speaker}</span>
      <div className="fluent-review__bubble">
        <p className="fluent-review__en" lang="en">
          {line.en}
        </p>
        <p className="fluent-review__ka" lang="ka">
          {line.ka}
        </p>
      </div>
      {line.audioKey && (
        <AudioButton
          size="sm"
          playing={audio.playing}
          onClick={() => (audio.playing ? audio.stop() : audio.play())}
          label={t('lesson.review.audio_aria')}
        />
      )}
    </li>
  );
}
